import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Check, X, CornerUpLeft, Search, UserPlus } from 'lucide-react'
import type { FpdDay, FpdRequest, FpdStatus, Scope } from '../../core/types'
import { useAuth } from '../../core/auth'
import { SCOPE_LABELS } from '../../core/caps'
import { useDirectory } from '../admin/model'
import { Loading, Empty, ErrorState } from '../../ui/States'
import { Text, Area } from '../../ui/Field'
import {
  useFpdDays, useFpdConfig, useMyRequests, useBuildingRequests, useRequest, useRequestEvents,
  saveRequest, markDone, decide, deleteDraft, buildingsFor, usePost,
} from './model'
import {
  schoolYearOf, daysFor, upcoming, isoToday, hoursOf, hoursLabel, toMin, fromMin, clock, formGaps, onCalendar,
  sendStatus, statusText, statusTone, waitingOnPrincipal, flexAlerts, type RequestForm,
} from './logic'

/**
 * Flex PD requests: a teacher asks, their building's principal approves,
 * sends back with a reason, or denies; after the day the teacher says what
 * they did and the principal verifies it, and only then do the hours count.
 * A retroactive request (done before asking) goes straight to verifying.
 */

const fmt = (iso: string, o: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' }) =>
  new Date(iso + 'T12:00:00').toLocaleDateString(undefined, o)
const when = (r: Pick<FpdRequest, 'date' | 'startMin' | 'endMin'>) =>
  `${fmt(r.date)} · ${clock(r.startMin)} to ${clock(r.endMin)} · ${hoursLabel(hoursOf(r))}`

function StatusPill({ r, viewer }: { r: FpdRequest; viewer: 'teacher' | 'principal' }) {
  const tone = statusTone(r.status)
  const Icon = r.status === 'verified' ? Check : r.status === 'denied' ? X : r.status === 'returned' ? CornerUpLeft : null
  return (
    <span className={`pill${tone === 'plain' ? '' : ` pill--${tone}`}`}>
      {Icon && <Icon size={12} aria-hidden="true" />}{statusText(r, viewer)}
    </span>
  )
}

function useMe() {
  const { profile, user, displayName } = useAuth()
  return { email: (user?.email ?? '').toLowerCase(), name: profile?.displayName ?? displayName, profile }
}

/* ---------------- the teacher's list (on the Flex PD home) ---------------- */

export function MyRequests({ yearId }: { yearId: string }) {
  const me = useMe()
  const rows = useMyRequests(yearId, me.email, true)
  const mayRequest = buildingsFor(me.profile?.caps, me.profile?.districtAdmin, 'flexpd.request').length > 0
  if (!mayRequest) return null
  if (rows === null) return <Loading label="Loading your requests" />
  const sorted = [...rows].sort((a, b) => b.date.localeCompare(a.date))
  return (
    <>
      <div className="fpd__head">
        <h2 className="work__heading" style={{ margin: 0 }}>Your requests</h2>
        <Link to="/flex-pd/new" className="btn" style={{ width: 'auto', textDecoration: 'none' }}>+ New request</Link>
      </div>
      {sorted.length === 0 ? (
        <Empty title="No requests yet" body="Plan PD on a flex day, or log PD you already did as a retroactive request." />
      ) : (
        <ul className="work">
          {sorted.map((r) => (
            <li key={r.id} className={`work__row${r.status === 'returned' || r.status === 'approved' ? ' is-live' : ''}`}>
              <div className="work__main">
                <Link to={`/flex-pd/${r.id}`} className="work__title">{r.title || 'Untitled request'}</Link>
                <div className="work__sub">{when(r)}{r.collaborative ? ' · collaborative' : ''}{r.retroactive ? ' · retroactive' : ''}</div>
              </div>
              <StatusPill r={r} viewer="teacher" />
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

/* ---------------- one request: new, edit, or read ---------------- */

/** A new request, or one started from a collaboration post (?post=), filled in from it. */
export function NewRequest() {
  const [params] = useSearchParams()
  const postId = params.get('post') ?? ''
  const post = usePost(postId)
  const me = useMe()
  if (postId && post === undefined) return <Loading label="Loading the collaboration" />
  if (!post) return <RequestEditor existing={undefined} />
  const others = [{ email: post.organizerEmail, name: post.organizerName }, ...post.members]
    .filter((m) => m.email !== me.email).map((m) => ({ email: m.email, name: m.name }))
  const [headline, ...rest] = post.idea.split('\n')
  return <RequestEditor existing={undefined} prefill={{
    date: post.date, startMin: post.startMin, endMin: post.endMin, focus: post.focus, place: post.place,
    title: headline ?? '', description: rest.join('\n').trim() || post.idea, collaborative: true, collaborators: others, postId: post.id,
  }} />
}

export function RequestPage() {
  const { id = '' } = useParams()
  const me = useMe()
  const r = useRequest(id)
  const [editing, setEditing] = useState(false)
  if (r === undefined) return <Loading label="Loading the request" />
  if (r === null) return <Empty title="That request is not here" body="It may have been a draft that was deleted, or it is not yours to see." />
  const mine = r.teacherEmail === me.email
  if (mine && (editing || r.status === 'draft')) return <RequestEditor existing={r} onCancel={() => setEditing(false)} />
  return <RequestView r={r} mine={mine} onEdit={() => setEditing(true)} />
}

const BLANK: RequestForm = {
  building: 'hs', date: '', startMin: 0, endMin: 0, place: '', focus: '', title: '', description: '',
  collaborative: false, collaborators: [], retroactive: false, evidence: '',
}

function RequestEditor({ existing, onCancel, prefill }: {
  existing: FpdRequest | undefined; onCancel?: () => void; prefill?: Partial<RequestForm> & { postId?: string }
}) {
  const me = useMe()
  const nav = useNavigate()
  const yearId = existing?.yearId ?? schoolYearOf().id
  const myBuildings = buildingsFor(me.profile?.caps, me.profile?.districtAdmin, 'flexpd.request')
  const cfg = useFpdConfig()
  const days = useFpdDays(yearId)
  const [f, setF] = useState<RequestForm & { postId?: string }>(() => existing
    ? { building: existing.building, date: existing.date, startMin: existing.startMin, endMin: existing.endMin, place: existing.place,
        focus: existing.focus, title: existing.title, description: existing.description, collaborative: existing.collaborative,
        collaborators: existing.collaborators, retroactive: existing.retroactive, evidence: existing.evidence,
        ...(existing.postId ? { postId: existing.postId } : {}) }
    : { ...BLANK, building: myBuildings[0] ?? 'hs', ...prefill })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const set = <K extends keyof RequestForm>(k: K, v: RequestForm[K]) => setF((x) => ({ ...x, [k]: v }))

  if (!cfg || days === null) return <Loading label="Loading the form" />
  if (!existing && myBuildings.length === 0) return <Empty title="Requests are for teachers" body="You need the Flex PD Teacher role to make a request." />

  const flexDays = upcoming(daysFor(days.filter((d) => !d.closed), [f.building]))
  const gaps = formGaps(f)
  const off = !!f.date && !onCalendar(f.date, f.building, days)
  const from: FpdStatus = existing?.status ?? 'draft'
  const send = sendStatus(f, from)
  const approvedEdit = existing?.status === 'approved'
  const active = (l: typeof cfg.focus) => l.filter((o) => !o.retired).map((o) => o.name)

  const run = (fn: () => Promise<string | void>) => async () => {
    setBusy(true); setErr(null)
    try {
      const id = await fn()
      // A new request gets its own address; an edit closes the form and shows the request.
      if (id && id !== existing?.id) nav(`/flex-pd/${id}`, { replace: true })
      else onCancel?.()
    }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(false) }
  }
  const me2 = { email: me.email, name: me.name }

  return (
    <>
      <Link to="/flex-pd" className="backlink">← Flex PD</Link>
      <h1 className="page-title">{existing ? (approvedEdit ? 'Change an approved request' : 'Edit request') : 'New Flex PD request'}</h1>
      {approvedEdit && <p className="page-sub">Changing it sends it back to your principal to approve again.</p>}
      {existing?.status === 'returned' && existing.note && (
        <div className="notice notice--warning" style={{ marginTop: 'var(--s-3)' }}>
          <span aria-hidden="true">⚠</span>
          <div><strong>Sent back{existing.decidedBy ? ` by ${existing.decidedBy}` : ''}:</strong> {existing.reasons.join(' · ')}{existing.reasons.length && existing.note ? '. ' : ''}{existing.note}</div>
        </div>
      )}
      {err && <ErrorState body={`Not saved. ${err}`} />}

      <div className="pform" style={{ marginTop: 'var(--s-4)' }}>
        <section className="card pform__step">
          <header className="pform__head"><span className="pform__n" aria-hidden="true">1</span>
            <div><h2 className="pform__title">When</h2><p className="pform__hint">Pick a flex day, or another date (your principal sees that it is not on the calendar).</p></div>
          </header>
          <div className="pform__body">
            <label className="pick" style={{ marginBottom: 'var(--s-3)' }}>
              <input type="checkbox" checked={f.retroactive} onChange={(e) => set('retroactive', e.target.checked)} />
              <span className="pick__label"><strong>I already did this PD</strong> (retroactive): it goes straight to your principal to verify.</span>
            </label>
            {myBuildings.length > 1 && !existing && (
              <>
                <div className="opt__label" style={{ margin: 0 }}>Counts for</div>
                <div className="chips" style={{ margin: '4px 0 var(--s-3)' }}>
                  {myBuildings.map((b) => <button key={b} type="button" aria-pressed={f.building === b} className={`chip${f.building === b ? ' is-on' : ''}`} onClick={() => set('building', b as Scope)}>{SCOPE_LABELS[b]}</button>)}
                </div>
              </>
            )}
            {!f.retroactive && flexDays.length > 0 && (
              <>
                <div className="opt__label" style={{ margin: 0 }}>Flex days</div>
                <div className="chips" style={{ margin: '4px 0 var(--s-3)' }}>
                  {flexDays.slice(0, 8).map((d: FpdDay) => <button key={d.id} type="button" aria-pressed={f.date === d.date} className={`chip${f.date === d.date ? ' is-on' : ''}`} onClick={() => set('date', d.date)}>{fmt(d.date)}</button>)}
                </div>
              </>
            )}
            <div className="host__grid">
              <Text label="Date" type="date" value={f.date} onChange={(v) => set('date', v)} required />
              <Text label="Starts" type="time" value={f.startMin || f.endMin ? fromMin(f.startMin) : ''} onChange={(v) => set('startMin', toMin(v) || 0)} required />
              <Text label="Ends" type="time" value={f.endMin ? fromMin(f.endMin) : ''} onChange={(v) => set('endMin', toMin(v) || 0)} required />
            </div>
            <p className="opt__hint" style={{ marginTop: '-8px' }}>
              {f.endMin > f.startMin ? <>That is <strong>{hoursLabel(hoursOf(f))}</strong>.</> : 'Hours are worked out from the times.'}
              {off && <> <strong>Not a flex day</strong> for {SCOPE_LABELS[f.building]}: your principal will see that.</>}
            </p>
          </div>
        </section>

        <section className="card pform__step">
          <header className="pform__head"><span className="pform__n" aria-hidden="true">2</span>
            <div><h2 className="pform__title">What</h2><p className="pform__hint">What you will learn or work on, and where.</p></div>
          </header>
          <div className="pform__body">
            <Text label="Title" value={f.title} onChange={(v) => set('title', v)} required placeholder="e.g. Rewriting our 9th grade argument rubric" />
            <ChoiceField label="Learning focus" value={f.focus} options={active(cfg.focus)} onChange={(v) => set('focus', v)} />
            <ChoiceField label="Place" value={f.place} options={active(cfg.places)} onChange={(v) => set('place', v)} other="Somewhere else (type it)" />
            <Area label="What you will do" value={f.description} onChange={(v) => set('description', v)} required
              placeholder="The work, who it is for, and what you will have at the end." />
          </div>
        </section>

        <section className="card pform__step">
          <header className="pform__head"><span className="pform__n" aria-hidden="true">3</span>
            <div><h2 className="pform__title">With whom</h2><p className="pform__hint">Collaborative hours count toward the collaborative requirement.</p></div>
          </header>
          <div className="pform__body">
            <div className="chips">
              <button type="button" aria-pressed={!f.collaborative} className={`chip${!f.collaborative ? ' is-on' : ''}`} onClick={() => set('collaborative', false)}>On my own</button>
              <button type="button" aria-pressed={f.collaborative} className={`chip${f.collaborative ? ' is-on' : ''}`} onClick={() => set('collaborative', true)}>With other teachers</button>
            </div>
            {f.collaborative && <Collaborators value={f.collaborators} me={me.email} onChange={(v) => set('collaborators', v)} />}
          </div>
        </section>

        {(f.retroactive || (existing && sendStatus(f, from) === 'completed')) && (
          <section className="card pform__step">
            <header className="pform__head"><span className="pform__n" aria-hidden="true">4</span>
              <div><h2 className="pform__title">What you did <span className="req">*</span></h2><p className="pform__hint">Your principal verifies from this. What happened, and what came out of it.</p></div>
            </header>
            <div className="pform__body">
              <Area label="Evidence" value={f.evidence} onChange={(v) => set('evidence', v)} required
                placeholder="e.g. Met 8:00 to 11:30 with the ELA team; drafted the rubric; link to the shared doc." />
            </div>
          </section>
        )}

        <section className="card pform__submit">
          <div style={{ display: 'flex', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
            <button className="btn" style={{ width: 'auto' }} disabled={busy || gaps.length > 0}
              onClick={run(() => saveRequest(yearId, me2, f, existing, send))}>
              {busy ? 'Sending…' : send === 'completed' ? 'Send to your principal to verify' : approvedEdit ? 'Send changes for approval' : 'Send to your principal'}
            </button>
            {!approvedEdit && (
              <button className="btn btn--ghost" style={{ width: 'auto' }} disabled={busy || !f.title.trim() || !f.date || !(f.endMin > f.startMin)}
                onClick={run(() => saveRequest(yearId, me2, f, existing))}>
                {existing && existing.status !== 'draft' ? 'Save changes' : 'Save draft'}
              </button>
            )}
            {existing?.status === 'draft' && (
              <button className="btn btn--ghost" style={{ width: 'auto' }} disabled={busy}
                onClick={run(async () => { await deleteDraft(existing.id); nav('/flex-pd', { replace: true }) })}>Delete draft</button>
            )}
            {existing && existing.status !== 'draft' && <button className="linkish" disabled={busy} onClick={onCancel}>Cancel</button>}
          </div>
          {gaps.length > 0 && <p className="opt__hint">Before you can send it: {gaps.join(', ')}.</p>}
        </section>
      </div>
    </>
  )
}

/** A list the district keeps, plus a typed answer when none fits. */
function ChoiceField({ label, value, options, onChange, other }: { label: string; value: string; options: string[]; onChange: (v: string) => void; other?: string }) {
  const typed = !!other && value !== '' && !options.includes(value)
  const [typing, setTyping] = useState(typed)
  return (
    <div style={{ marginBottom: 'var(--s-4)' }}>
      <label className="opt__label" style={{ margin: 0, display: 'block' }}>{label} <span className="req">*</span></label>
      <select className="opt__input" style={{ width: '100%', marginTop: 4 }} value={typing ? '__other' : value}
        onChange={(e) => { if (e.target.value === '__other') { setTyping(true); onChange('') } else { setTyping(false); onChange(e.target.value) } }}>
        <option value="">Choose…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
        {/* A retired option the request already used still shows, with its words. */}
        {value && !options.includes(value) && !typing && <option value={value}>{value}</option>}
        {other && <option value="__other">{other}</option>}
      </select>
      {typing && <input className="opt__input" style={{ width: '100%', marginTop: 4 }} value={value} autoFocus aria-label={`${label}: type it`}
        onChange={(e) => onChange(e.target.value)} placeholder="Type it in" />}
    </div>
  )
}

function Collaborators({ value, me, onChange }: { value: Array<{ email: string; name: string }>; me: string; onChange: (v: Array<{ email: string; name: string }>) => void }) {
  const { rows } = useDirectory()
  const [q, setQ] = useState('')
  const picked = new Set(value.map((v) => v.email))
  const matches = q.trim().length < 2 ? [] : (rows ?? [])
    .filter((s) => s.active !== false && s.email !== me && !picked.has(s.email) && s.displayName.toLowerCase().includes(q.trim().toLowerCase()))
    .slice(0, 6)
  return (
    <div style={{ marginTop: 'var(--s-3)' }}>
      {value.length > 0 && (
        <ul className="chips" style={{ listStyle: 'none', padding: 0, margin: '0 0 var(--s-2)' }}>
          {value.map((c) => (
            <li key={c.email} className="chip is-on" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {c.name}
              <button type="button" className="fpd__x" aria-label={`Remove ${c.name}`} onClick={() => onChange(value.filter((x) => x.email !== c.email))}><X size={12} aria-hidden="true" /></button>
            </li>
          ))}
        </ul>
      )}
      <label className="sel__search" style={{ maxWidth: 360 }}>
        <UserPlus size={15} aria-hidden="true" />
        <input className="opt__input" placeholder="Add a teacher: type a name" aria-label="Add a collaborator" value={q} onChange={(e) => setQ(e.target.value)} />
      </label>
      {matches.length > 0 && (
        <ul className="work" style={{ marginTop: 'var(--s-2)' }}>
          {matches.map((s) => (
            <li key={s.email} className="work__row">
              <button type="button" className="row-link" onClick={() => { onChange([...value, { email: s.email, name: s.displayName }]); setQ('') }}>{s.displayName}</button>
              <span className="work__sub" style={{ marginLeft: 'auto' }}>{(s.buildings ?? []).map((b) => SCOPE_LABELS[b] ?? b).join(', ')}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="opt__hint">Each of them makes their own request for the same day; their own principal approves their part.</p>
    </div>
  )
}

/** A request, read-only, with what the person looking may do next. */
function RequestView({ r, mine, onEdit }: { r: FpdRequest; mine: boolean; onEdit: () => void }) {
  const me = useMe()
  const days = useFpdDays(r.yearId)
  const events = useRequestEvents(r.id, true)
  const deciderHere = !mine && buildingsFor(me.profile?.caps, me.profile?.districtAdmin, 'flexpd.approve').includes(r.building)
  const off = days !== null && !onCalendar(r.date, r.building, days)
  return (
    <>
      <Link to={mine ? '/flex-pd' : '/flex-pd/review'} className="backlink">← {mine ? 'Flex PD' : 'Requests to review'}</Link>
      <div className="fpd__head">
        <div>
          <h1 className="page-title">{r.title || 'Untitled request'}</h1>
          <p className="page-sub">{mine ? 'Your request' : `${r.teacherName} · ${SCOPE_LABELS[r.building]}`}</p>
        </div>
        <StatusPill r={r} viewer={mine ? 'teacher' : 'principal'} />
      </div>

      {r.note && (r.status === 'returned' || r.status === 'denied') && (
        <div className={`notice ${r.status === 'denied' ? 'notice--critical' : 'notice--warning'}`} style={{ marginTop: 'var(--s-3)' }}>
          <span aria-hidden="true">⚠</span>
          <div><strong>{r.status === 'denied' ? 'Denied' : 'Sent back'}{r.decidedBy ? ` by ${r.decidedBy}` : ''}:</strong> {r.reasons.join(' · ')}{r.reasons.length && r.note ? '. ' : ''}{r.note}</div>
        </div>
      )}

      <section className="card" style={{ marginTop: 'var(--s-4)' }}>
        <dl className="host__dl fpd__dl">
          <dt>When</dt><dd>{when(r)}{off && <> · <strong>not a flex day</strong> for {SCOPE_LABELS[r.building]}</>}</dd>
          <dt>Focus</dt><dd>{r.focus}</dd>
          <dt>Place</dt><dd>{r.place}</dd>
          <dt>With</dt><dd>{r.collaborative ? (r.collaborators.map((c) => c.name).join(', ') || 'Others, not named') : 'On their own'}</dd>
          {r.retroactive && <><dt>Asked</dt><dd>Retroactive: done before asking</dd></>}
        </dl>
        <h2 className="ov__h" style={{ marginTop: 'var(--s-4)' }}>What {mine ? 'you' : 'they'} will do</h2>
        <p className="ov__p" style={{ whiteSpace: 'pre-wrap' }}>{r.description}</p>
        {r.evidence && (
          <>
            <h2 className="ov__h" style={{ marginTop: 'var(--s-4)' }}>What {mine ? 'you' : 'they'} did</h2>
            <p className="ov__p" style={{ whiteSpace: 'pre-wrap' }}>{r.evidence}</p>
          </>
        )}
      </section>

      {mine && <TeacherActions r={r} onEdit={onEdit} />}
      {deciderHere && waitingOnPrincipal(r) && <DecisionPanel r={r} />}

      {events.length > 0 && (
        <>
          <h2 className="work__heading">History</h2>
          <ul className="phist">
            {events.map((e) => (
              <li key={e.id}>
                <strong>{e.byName}</strong> {EVENT_WORDS[e.action]}
                <span className="phist__when"> · {(e.at as { toDate?: () => Date })?.toDate?.().toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) ?? ''}</span>
                {(e.reasons.length > 0 || e.note) && <div className="phist__note">{[e.reasons.join(' · '), e.note].filter(Boolean).join('. ')}</div>}
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  )
}

const EVENT_WORDS: Record<string, string> = {
  submitted: 'sent it for approval', resubmitted: 'sent it again', approved: 'approved it', returned: 'sent it back',
  denied: 'denied it', completed: 'said it happened, with evidence', verified: 'verified it: the hours count',
}

function TeacherActions({ r, onEdit }: { r: FpdRequest; onEdit: () => void }) {
  const me = useMe()
  const [evidence, setEvidence] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const past = r.date <= isoToday()
  if (r.status === 'returned') {
    return <div className="opt__bar"><button className="btn" style={{ width: 'auto' }} onClick={onEdit}>Revise and send again</button></div>
  }
  if (r.status !== 'approved') return null
  return (
    <section className="card card--action">
      <h2 className="ov__h">{past ? 'Done? Say what you did' : 'After the day'}</h2>
      <p className="ov__p">{past
        ? 'Your principal verifies from this, and then the hours count.'
        : `On or after ${fmt(r.date)}, come back and say what you did; your principal verifies it, and then the hours count.`}</p>
      {err && <ErrorState body={`Not saved. ${err}`} />}
      {past && (
        <>
          <Area label="What you did" value={evidence} onChange={setEvidence} required placeholder="What happened, and what came out of it (a link to the work is good)." />
          <div style={{ display: 'flex', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
            <button className="btn" style={{ width: 'auto' }} disabled={busy || !evidence.trim()} onClick={async () => {
              setBusy(true); setErr(null)
              try { await markDone(r, me, evidence) } catch (e) { setErr(e instanceof Error ? e.message : String(e)) } finally { setBusy(false) }
            }}>{busy ? 'Sending…' : 'Send to be verified'}</button>
          </div>
        </>
      )}
      <p className="opt__hint">Plans changed? <button className="linkish" onClick={onEdit}>Change the request</button> (it goes back for approval).</p>
    </section>
  )
}

/** The principal: approve or verify, send back with a reason, or deny. */
function DecisionPanel({ r }: { r: FpdRequest }) {
  const me = useMe()
  const cfg = useFpdConfig()
  const [mode, setMode] = useState<'back' | 'deny' | null>(null)
  const [note, setNote] = useState('')
  const [reasons, setReasons] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const verifying = r.status === 'completed'
  const go = (to: 'approved' | 'returned' | 'denied' | 'verified') => async () => {
    setBusy(true); setErr(null)
    try { await decide(r, me, to, note, to === 'returned' ? reasons : []); setMode(null); setNote(''); setReasons([]) }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)) } finally { setBusy(false) }
  }
  return (
    <section className="card card--action">
      <h2 className="ov__h">{verifying ? 'Did it happen?' : 'Your decision'}</h2>
      <p className="ov__p">{verifying ? 'Verify it and the hours count toward this year.' : 'Approve it, or send it back with what to change.'}</p>
      {err && <ErrorState body={`Not saved. ${err}`} />}
      {mode === null && (
        <div style={{ display: 'flex', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
          <button className="btn" style={{ width: 'auto' }} disabled={busy} onClick={go(verifying ? 'verified' : 'approved')}>
            <Check size={16} aria-hidden="true" /> {busy ? 'Saving…' : verifying ? `Verify ${hoursLabel(hoursOf(r))}` : 'Approve'}
          </button>
          <button className="btn btn--ghost" style={{ width: 'auto' }} disabled={busy} onClick={() => setMode('back')}><CornerUpLeft size={16} aria-hidden="true" /> Send back</button>
          <button className="btn btn--ghost" style={{ width: 'auto' }} disabled={busy} onClick={() => setMode('deny')}><X size={16} aria-hidden="true" /> Deny</button>
        </div>
      )}
      {mode === 'back' && (
        <>
          <div className="opt__label" style={{ margin: 0 }}>Why (pick any)</div>
          <div className="chips" style={{ margin: '4px 0 var(--s-3)' }}>
            {(cfg?.sendBack ?? []).filter((o) => !o.retired).map((o) => {
              const on = reasons.includes(o.name)
              return <button key={o.name} type="button" aria-pressed={on} className={`chip${on ? ' is-on' : ''}`}
                onClick={() => setReasons((x) => (on ? x.filter((y) => y !== o.name) : [...x, o.name]))}>{o.name}</button>
            })}
          </div>
          <Area label="Note to the teacher" value={note} onChange={setNote} placeholder="What to change, in a sentence." />
          <div style={{ display: 'flex', gap: 'var(--s-2)' }}>
            <button className="btn" style={{ width: 'auto' }} disabled={busy || (!note.trim() && reasons.length === 0)} onClick={go('returned')}>{busy ? 'Sending…' : 'Send back'}</button>
            <button className="linkish" onClick={() => setMode(null)}>Cancel</button>
          </div>
        </>
      )}
      {mode === 'deny' && (
        <>
          <Area label="Why it is denied" value={note} onChange={setNote} required placeholder="The teacher sees this." />
          <div style={{ display: 'flex', gap: 'var(--s-2)' }}>
            <button className="btn" style={{ width: 'auto' }} disabled={busy || !note.trim()} onClick={go('denied')}>{busy ? 'Saving…' : 'Deny'}</button>
            <button className="linkish" onClick={() => setMode(null)}>Cancel</button>
          </div>
        </>
      )}
    </section>
  )
}

/* ---------------- principals: the queue; coaches and district: everything ---------------- */

export function ReviewQueue() {
  const { profile } = useAuth()
  const yearId = schoolYearOf().id
  const deciding = buildingsFor(profile?.caps, profile?.districtAdmin, 'flexpd.approve')
  const seeing = buildingsFor(profile?.caps, profile?.districtAdmin, 'flexpd.approve', 'flexpd.view')
  const rows = useBuildingRequests(yearId, seeing)
  const days = useFpdDays(yearId)
  const me = (useAuth().user?.email ?? '').toLowerCase()
  const [show, setShow] = useState<'waiting' | 'all'>(deciding.length ? 'waiting' : 'all')
  const [q, setQ] = useState('')
  const visible = useMemo(() => (rows ?? []).filter((r) => r.status !== 'draft'), [rows])
  if (!seeing.length) return <Empty title="Nothing to review" body="Principals, learning coaches and the district office see requests here." />
  if (rows === null || days === null) return <Loading label="Loading requests" />
  const mineToDecide = (r: FpdRequest) => deciding.includes(r.building) && r.teacherEmail !== me && waitingOnPrincipal(r)
  const waiting = visible.filter(mineToDecide)
  const shown = (show === 'waiting' ? waiting : visible)
    .filter((r) => !q || `${r.teacherName} ${r.title} ${r.focus}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (show === 'waiting' ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date)))
  return (
    <>
      <Link to="/flex-pd" className="backlink">← Flex PD</Link>
      <h1 className="page-title">{deciding.length ? 'Requests to review' : 'Flex PD requests'}</h1>
      <p className="page-sub">{seeing.map((b) => SCOPE_LABELS[b]).join(', ')} · {schoolYearOf().label}{deciding.length ? '' : ' · read only'}</p>
      <div className="fpd__filters">
        <label className="sel__search"><Search size={15} aria-hidden="true" />
          <input className="opt__input" placeholder="Search name, title or focus" aria-label="Search requests" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        <div className="chips" role="tablist" aria-label="Show">
          {deciding.length > 0 && <button role="tab" aria-selected={show === 'waiting'} className={`chip${show === 'waiting' ? ' is-on' : ''}`} onClick={() => setShow('waiting')}>Waiting on you ({waiting.length})</button>}
          <button role="tab" aria-selected={show === 'all'} className={`chip${show === 'all' ? ' is-on' : ''}`} onClick={() => setShow('all')}>Everything ({visible.length})</button>
        </div>
      </div>
      {shown.length === 0 ? (
        <Empty title={show === 'waiting' ? 'Nothing waiting on you' : 'No requests yet'} body={show === 'waiting' ? 'New requests and completed PD to verify show up here.' : 'Requests appear here once teachers send them.'} />
      ) : (
        <>
          <table className="table">
            <thead><tr><th>Teacher</th><th>Request</th><th>When</th><th className="num">Hours</th><th>Status</th></tr></thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id} className={mineToDecide(r) ? 'is-flag' : ''}>
                  <td><div style={{ fontWeight: 610 }}>{r.teacherName}</div><div className="work__sub">{SCOPE_LABELS[r.building]}</div></td>
                  <td><Link to={`/flex-pd/${r.id}`} className="row-link">{r.title || 'Untitled request'}</Link>
                    <div className="work__sub">{r.focus}{r.collaborative ? ' · collaborative' : ''}{r.retroactive ? ' · retroactive' : ''}</div></td>
                  <td>{fmt(r.date)}{!onCalendar(r.date, r.building, days) && <div className="work__sub">not a flex day</div>}</td>
                  <td className="num">{hoursOf(r)}</td>
                  <td><StatusPill r={r} viewer="principal" /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <ul className="work cards">
            {shown.map((r) => (
              <li key={r.id} className={`work__row${mineToDecide(r) ? ' is-live' : ''}`}>
                <div className="work__main">
                  <Link to={`/flex-pd/${r.id}`} className="work__title">{r.teacherName}: {r.title || 'Untitled request'}</Link>
                  <div className="work__sub">{when(r)}</div>
                  <div style={{ marginTop: 4 }}><StatusPill r={r} viewer="principal" /></div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  )
}

/** How many requests wait on this principal: the rail and tile count. */
export function useWaitingCount(): number {
  const { profile, user } = useAuth()
  const deciding = buildingsFor(profile?.caps, profile?.districtAdmin, 'flexpd.approve')
  const rows = useBuildingRequests(schoolYearOf().id, deciding)
  const me = (user?.email ?? '').toLowerCase()
  return (rows ?? []).filter((r) => r.teacherEmail !== me && waitingOnPrincipal(r)).length
}

/** What this person has to act on in Flex PD, for the bell. */
export function useFlexPdAlerts() {
  const { canUseTool, user, student } = useAuth()
  const on = !student && canUseTool('flexpd')
  const mine = useMyRequests(schoolYearOf().id, (user?.email ?? '').toLowerCase(), on)
  const waiting = useWaitingCount()
  return useMemo(() => (on ? flexAlerts(mine ?? [], waiting) : []), [on, mine, waiting])
}
